import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  LuMic,
  LuSend,
  LuX,
  LuCircleStop,
  LuSparkles,
  LuLayoutDashboard,
  LuLogIn,
  LuUserPlus,
  LuAudioLines,
} from 'react-icons/lu';
import useTranslation from '../../hooks/useTranslation';

/**
 * Xi Assistant — floating, integrated assistant panel.
 *
 * Voice is the real product contract: it records audio with MediaRecorder and
 * hands the blob to `onSendVoice`, which the host page wires to the existing
 * `sendVoiceToSpeechBot` endpoint (`/speak`). No reply is ever fabricated.
 *
 * Text: this deployment has no text-chat endpoint. When `onSendText` is not
 * supplied the composer is honest about it and points the user at voice. If a
 * text endpoint is added later, pass `onSendText(text, context)` and the panel
 * will use it with no other changes. See `docs/XI_ASSETS_AND_LIMITS.md`.
 */

// Xi's identity is the purple mascot from the design reference. Keep this
// asset-swappable: point it at an animated .gif/.webp/.svg or a Lottie
// player later without changing any layout.
const ASSISTANT_AVATAR = '/assets/mascot/xi-mascot-home.png';

const uid = () => `${Date.now()}-${Math.random().toString(16).slice(2)}`;

export default function XiAssistant({
  onSendVoice,
  onSendText,
  onCommand,
  onRequireLogin,
  isAuthenticated = true,
  context = '',
  initialGreeting = '',
  quickActions,
  className = '',
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [recording, setRecording] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showHint, setShowHint] = useState(true);

  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const avatar = ASSISTANT_AVATAR;

  const actions = useMemo(
    () =>
      quickActions || [
        { key: 'dashboard', label: t('Dashboard'), icon: <LuLayoutDashboard />, command: 'dashboard' },
        { key: 'record', label: t('Start recording'), icon: <LuAudioLines />, command: 'record' },
        { key: 'login', label: t('Sign in'), icon: <LuLogIn />, command: 'login' },
        { key: 'signup', label: t('Create account'), icon: <LuUserPlus />, command: 'sign up' },
      ],
    [quickActions, t]
  );

  const push = useCallback((role, text) => {
    setMessages((prev) => [...prev, { id: uid(), role, text }]);
  }, []);

  useEffect(() => {
    if (!open) return;
    if (messages.length === 0) {
      push('bot', initialGreeting || t('Hello — I am Xi. Tap the microphone and speak, and I will respond with the live wellbeing assistant.'));
    }
  }, [open, messages.length, initialGreeting, push, t]);

  useEffect(() => {
    if (!open) return;
    const timer = setTimeout(() => setShowHint(false), 6000);
    return () => clearTimeout(timer);
  }, [open]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading, open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const stopStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
  };

  useEffect(() => () => stopStream(), []);

  const finishRecording = useCallback(async () => {
    const blob = new Blob(chunksRef.current, { type: 'audio/ogg' });
    chunksRef.current = [];
    stopStream();
    setRecording(false);

    if (!blob.size) {
      setError(t('No audio was captured. Please try again.'));
      return;
    }
    if (typeof onSendVoice !== 'function') {
      setError(t('The assistant voice endpoint is not configured.'));
      return;
    }

    setLoading(true);
    setError('');
    try {
      const result = await onSendVoice(blob, { context });
      const text =
        result && typeof result === 'object'
          ? result.text || result.response || ''
          : typeof result === 'string'
          ? result
          : '';
      if (text) push('bot', text);
      else push('system', t('The assistant returned no text for that recording.'));
    } catch (err) {
      const message = err && err.message ? err.message : t('The assistant could not be reached.');
      setError(message);
      push('system', t('Voice message failed. Check your connection and try again.'));
    } finally {
      setLoading(false);
    }
  }, [context, onSendVoice, push, t]);

  const startRecording = useCallback(async () => {
    if (!isAuthenticated) {
      if (typeof onRequireLogin === 'function') onRequireLogin();
      return;
    }
    if (typeof onSendVoice !== 'function') {
      setError(t('The assistant voice endpoint is not configured.'));
      return;
    }
    setError('');
    try {
      if (window.speechSynthesis) window.speechSynthesis.cancel();
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      chunksRef.current = [];
      const recorder = new window.MediaRecorder(stream);
      recorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) chunksRef.current.push(event.data);
      };
      recorder.onstop = finishRecording;
      recorder.start();
      recorderRef.current = recorder;
      setRecording(true);
    } catch (err) {
      setRecording(false);
      stopStream();
      setError(
        err && err.name === 'NotAllowedError'
          ? t('Microphone access was blocked. Enable it in your browser settings to talk to Xi.')
          : t('Microphone unavailable. Check your device and try again.')
      );
    }
  }, [finishRecording, isAuthenticated, onRequireLogin, onSendVoice, t]);

  const stopRecording = useCallback(() => {
    const recorder = recorderRef.current;
    if (recorder && recorder.state === 'recording') recorder.stop();
    else finishRecording();
  }, [finishRecording]);

  const handleSendText = useCallback(
    (event) => {
      event.preventDefault();
      const text = draft.trim();
      if (!text || loading) return;
      if (!isAuthenticated) {
        if (typeof onRequireLogin === 'function') onRequireLogin();
        return;
      }
      push('user', text);
      setDraft('');
      if (typeof onSendText === 'function') {
        setLoading(true);
        setError('');
        Promise.resolve(onSendText(text, { context }))
          .then((result) => {
            const reply =
              result && typeof result === 'object' ? result.text || result.response : result;
            if (reply) push('bot', reply);
          })
          .catch((err) => setError(err && err.message ? err.message : t('Message failed.')))
          .finally(() => setLoading(false));
        return;
      }
      push(
        'system',
        t(
          'Text chat is not connected in this build. Voice messages use the live assistant endpoint — tap the microphone to speak.'
        )
      );
    },
    [context, draft, isAuthenticated, loading, onRequireLogin, onSendText, push, t]
  );

  const runAction = (action) => {
    if (action.command && typeof onCommand === 'function') {
      onCommand(action.command);
      push('user', action.label);
      push('system', t('Command sent.'));
      return;
    }
    if (typeof action.onClick === 'function') action.onClick();
  };

  const status = recording
    ? t('Listening…')
    : loading
    ? t('Thinking…')
    : t('Voice ready');

  return (
    <div className={`xi-assistant ${className}`.trim()}>
      {open ? (
        <section
          className="xi-assistant__panel"
          role="dialog"
          aria-modal="false"
          aria-label={t('Xi assistant')}
        >
          <header className="xi-assistant__head">
            <img className="xi-assistant__avatar" src={avatar} alt="" />
            <div className="xi-assistant__id">
              <div className="xi-assistant__name">{t('Xi Assistant')}</div>
              <div className="xi-assistant__status">
                <span className="xi-assistant__dot" aria-hidden="true" />
                {status}
              </div>
            </div>
            <button
              type="button"
              className="xi-icon-btn"
              onClick={() => setOpen(false)}
              aria-label={t('Minimise assistant')}
            >
              <LuX aria-hidden="true" />
            </button>
          </header>

          <div className="xi-assistant__messages" ref={scrollRef} aria-live="polite">
            {messages.map((message) => (
              <div
                key={message.id}
                className={`xi-assistant__msg xi-assistant__msg--${message.role}`}
              >
                {message.text}
              </div>
            ))}
            {loading ? (
              <div className="xi-assistant__msg xi-assistant__msg--bot" aria-label={t('Assistant is typing')}>
                <span className="xi-assistant__typing" aria-hidden="true">
                  <i />
                  <i />
                  <i />
                </span>
              </div>
            ) : null}
            {error ? (
              <div className="xi-assistant__msg xi-assistant__msg--system" role="alert">
                {error}
              </div>
            ) : null}
          </div>

          {actions.length > 0 ? (
            <div className="xi-assistant__quick" aria-label={t('Quick actions')}>
              {actions.map((action) => (
                <button
                  key={action.key}
                  type="button"
                  className="xi-assistant__chip"
                  onClick={() => runAction(action)}
                >
                  {action.icon ? <span aria-hidden="true">{action.icon}</span> : null}
                  {action.label}
                </button>
              ))}
            </div>
          ) : null}

          <form className="xi-assistant__composer" onSubmit={handleSendText}>
            <label className="xi-sr-only" htmlFor="xi-assistant-input">
              {t('Message Xi')}
            </label>
            <textarea
              id="xi-assistant-input"
              ref={inputRef}
              rows={1}
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && !event.shiftKey) {
                  event.preventDefault();
                  handleSendText(event);
                }
              }}
              placeholder={recording ? t('Listening…') : t('Message Xi…')}
              disabled={recording}
            />
            <button
              type="button"
              className={`xi-assistant__mic${recording ? ' is-recording' : ''}`}
              onClick={recording ? stopRecording : startRecording}
              aria-label={recording ? t('Stop recording') : t('Start voice message')}
              aria-pressed={recording}
              disabled={loading}
            >
              {recording ? <LuCircleStop aria-hidden="true" /> : <LuMic aria-hidden="true" />}
            </button>
            <button
              type="submit"
              className="xi-assistant__send"
              aria-label={t('Send message')}
              disabled={!draft.trim() || loading || recording}
            >
              <LuSend aria-hidden="true" />
            </button>
          </form>
        </section>
      ) : null}

      <button
        type="button"
        className="xi-assistant__launcher"
        onClick={() => {
          if (!isAuthenticated && typeof onRequireLogin === 'function') {
            onRequireLogin();
            return;
          }
          setOpen((prev) => !prev);
        }}
        aria-label={open ? t('Close assistant') : t('Open Xi assistant')}
        aria-expanded={open}
      >
        <img src={avatar} alt="" />
        {!open && showHint ? (
          <span className="xi-assistant__hint">
            <LuSparkles aria-hidden="true" style={{ verticalAlign: '-2px', marginRight: 4 }} />
            {t('Talk to Xi')}
          </span>
        ) : null}
      </button>
    </div>
  );
}
